import { createContext, useContext, useState, useEffect } from 'react'
import { stytch } from '../config/stytch'
import authService from '../services/authService'
import subscriptionService from '../services/subscriptionService'

const AuthContext = createContext(null)

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [appUserId, setAppUserId] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Check if user is already authenticated on mount
    const checkAuth = async () => {
      try {
        const session = stytch.session.getSync()
        if (session) {
          // Get user data
          const userData = await stytch.user.getSync()
          setUser(userData)

          // Get app-specific user ID
          const userIdResult = await subscriptionService.getUserId()
          if (userIdResult.success) {
            setAppUserId(userIdResult.userId)
          }
        }
      } catch (error) {
        console.error('Auth check error:', error)
      } finally {
        setLoading(false)
      }
    }

    checkAuth()
  }, [])

  const getUserAppId = async () => {
    try {
      const result = await subscriptionService.getUserId()
      if (result.success) {
        setAppUserId(result.userId)
        return result.userId
      }
      return null
    } catch (error) {
      console.error('Error getting app user ID:', error)
      return null
    }
  }

  const login = async (email, password) => {
    try {
      const response = await stytch.passwords.authenticate({
        email,
        password,
        session_duration_minutes: 60 * 24 * 365, // 365 days
      })
      console.log('Login response:', response)

      // Store JWT and register with backend
      await authService.handleAuthSuccess(response, false)

      setUser(response.user)

      // Get app-specific user ID
      await getUserAppId()

      return { success: true }
    } catch (error) {
      console.error('Login error:', error)
      // Extract error_message from Stytch API response
      const errorMessage = error.error_message || error.message || 'An error occurred during login'
      return { success: false, error: errorMessage }
    }
  }

  const signup = async (email, password) => {
    try {
      const response = await stytch.passwords.create({
        email,
        password,
        session_duration_minutes: 60 * 24 * 365, // 365 days
      })
      console.log('Signup response:', response)

      // Store JWT and register with backend
      await authService.handleAuthSuccess(response, true)

      setUser(response.user)

      // Get app-specific user ID
      await getUserAppId()

      return { success: true }
    } catch (error) {
      console.error('Signup error:', error)
      // Extract error_message from Stytch API response
      const errorMessage = error.error_message || error.message || 'An error occurred during signup'
      return { success: false, error: errorMessage }
    }
  }

  const logout = async () => {
    try {
      await stytch.session.revoke()

      // Clear JWT from localStorage
      authService.clearJWT()

      // Clear app user ID
      setAppUserId(null)
      localStorage.removeItem('app_user_id')

      // Clear subscription cache
      subscriptionService.clearSubscriptionCache()

      setUser(null)
      return { success: true }
    } catch (error) {
      console.error('Logout error:', error)
      return { success: false, error: error.message }
    }
  }

  const loginWithGoogle = async () => {
    try {
      const redirectUrl = import.meta.env.VITE_OAUTH_REDIRECT_URL || 'http://localhost:3000/authenticate'

      // Start OAuth flow - this will redirect to Google
      await stytch.oauth.google.start({
        login_redirect_url: redirectUrl,
        signup_redirect_url: redirectUrl,
      })

      return { success: true }
    } catch (error) {
      console.error('Google OAuth error:', error)
      const errorMessage = error.error_message || error.message || 'An error occurred during Google login'
      return { success: false, error: errorMessage }
    }
  }

  const value = {
    user,
    setUser,
    appUserId,
    getUserAppId,
    loading,
    login,
    signup,
    logout,
    loginWithGoogle,
    isAuthenticated: !!user,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}
